import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { KeyRound, CheckCircle2, XCircle, Loader2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const KakoTokenManager = ({ onTokenUpdated }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newToken, setNewToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/agency/token/status');
      setStatus(response.data);
      // Auto-expande se o token estiver inválido ou perto de expirar (<= 7 dias)
      if (!response.data.valid || response.data.expiring_soon) {
        setExpanded(true);
      }
    } catch (error) {
      console.error('Erro ao verificar status do token:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToken = async () => {
    if (!newToken.trim()) {
      toast.error('Cole o novo token antes de salvar');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/agency/token', { token: newToken.trim() });
      toast.success(response.data.message);
      setNewToken('');
      setExpanded(false);
      await loadStatus();
      if (onTokenUpdated) onTokenUpdated();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao atualizar token';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="border border-gray-200">
        <CardContent className="py-3 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Verificando token da agência...
        </CardContent>
      </Card>
    );
  }

  const isValid = status?.valid;

  return (
    <Card className={`border-2 ${isValid ? 'border-green-200 bg-green-50/50' : 'border-red-300 bg-red-50/50'}`} data-testid="kako-token-manager">
      <CardContent className="py-3 space-y-3">
        {/* Linha de status */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <KeyRound className={`w-5 h-5 ${isValid ? 'text-green-600' : 'text-red-600'}`} />
            <span className="font-semibold text-sm">Token da Agência KAKO</span>
            {isValid ? (
              <Badge className="bg-green-600 text-white" data-testid="token-status-badge">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Válido
              </Badge>
            ) : (
              <Badge className="bg-red-600 text-white animate-pulse" data-testid="token-status-badge">
                <XCircle className="w-3 h-3 mr-1" /> {status?.message || 'Expirado'}
              </Badge>
            )}
            {status?.agency_name && (
              <span className="text-xs text-gray-500">({status.agency_name})</span>
            )}
            {isValid && status?.days_remaining != null && (
              status.expiring_soon ? (
                <Badge className="bg-amber-500 text-white animate-pulse" data-testid="token-expiry-badge">
                  ⚠️ Expira em {status.days_remaining === 0 ? '< 1 dia' : `${status.days_remaining} dia${status.days_remaining === 1 ? '' : 's'}`}
                </Badge>
              ) : (
                <span className="text-xs text-gray-400" data-testid="token-expiry-info">
                  expira em {status.days_remaining} dias
                </span>
              )
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={loadStatus} title="Reverificar" data-testid="token-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              data-testid="token-toggle-btn"
            >
              {expanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
              {expanded ? 'Fechar' : 'Substituir Token'}
            </Button>
          </div>
        </div>

        {/* Detalhes / formulário */}
        {expanded && (
          <div className="space-y-2 pt-2 border-t border-gray-200">
            <div className="text-xs text-gray-500 space-y-1">
              {status?.masked_token && (
                <p>Token atual: <code className="bg-gray-100 px-1 rounded">{status.masked_token}</code></p>
              )}
              {status?.updated_at && (
                <p>Última atualização: {new Date(status.updated_at).toLocaleString('pt-BR')}</p>
              )}
              {status?.expires_at && (
                <p>
                  Validade do token: <b>{new Date(status.expires_at).toLocaleDateString('pt-BR')}</b>
                  {status?.days_remaining != null && ` (${status.days_remaining} dia${status.days_remaining === 1 ? '' : 's'} restante${status.days_remaining === 1 ? '' : 's'})`}
                </p>
              )}
              <p className="text-orange-600 font-medium">
                Cole o novo token abaixo. Ele será validado na API KAKO antes de salvar — efeito imediato, sem redeploy.
              </p>
            </div>
            <div className="flex gap-2 flex-col sm:flex-row">
              <Input
                type="text"
                placeholder="Cole o novo token aqui (eyJhbGciOi...)"
                value={newToken}
                onChange={(e) => setNewToken(e.target.value)}
                className="font-mono text-xs"
                data-testid="new-token-input"
              />
              <Button
                onClick={handleSaveToken}
                disabled={saving || !newToken.trim()}
                className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
                data-testid="save-token-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Validando...</>
                ) : (
                  'Validar e Salvar'
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KakoTokenManager;
